import { FileText, Camera, Tag } from 'lucide-react';
import { HotspotObject, ViewPosition } from '../types';

// Centralized color configuration for object types
export const OBJECT_TYPE_COLORS = {
  note: {
    primary: '#22c55e', // green-500
    background: 'bg-green-500 hover:bg-green-600',
    badge: 'bg-green-100 text-green-800 border-green-200',
  },
  image: {
    primary: '#6b7280', // gray-500
    background: 'bg-gray-400 hover:bg-gray-500',
    badge: 'bg-gray-100 text-gray-800 border-gray-200',
  },
  label: {
    primary: '#3b82f6', // blue-500
    background: 'bg-blue-500 hover:bg-blue-600',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
  },
} as const;

// Distance threshold for grouping hotspots (in degrees)
export const HOTSPOT_GROUP_THRESHOLD = 5; // Adjust this value based on testing

export interface SimplePosition {
  pitch: number;
  yaw: number;
}

export interface HotspotGroup {
  id: string;
  centerPosition: SimplePosition;
  hotspotIds: number[];
  objectCount: number;
}

/**
 * Calculate distance between two positions in degrees
 */
export function calculateDistance(pos1: SimplePosition, pos2: SimplePosition): number {
  const pitchDiff = Math.abs(pos1.pitch - pos2.pitch);
  const yawDiff = Math.abs(pos1.yaw - pos2.yaw);

  // Handle yaw wraparound (e.g., -179 to 179 degrees)
  const normalizedYawDiff = Math.min(yawDiff, 360 - yawDiff);

  return Math.sqrt(pitchDiff * pitchDiff + normalizedYawDiff * normalizedYawDiff);
}

/**
 * Find existing hotspots within the grouping threshold of a position
 */
export function findNearbyHotspots(
  position: SimplePosition,
  existingHotspots: Array<{ id: number; pitch: number; yaw: number }>
): number[] {
  return existingHotspots
    .filter((hotspot) => {
      const distance = calculateDistance(position, { pitch: hotspot.pitch, yaw: hotspot.yaw });
      return distance <= HOTSPOT_GROUP_THRESHOLD;
    })
    .map((hotspot) => hotspot.id);
}

/**
 * Group hotspots by proximity
 */
export function groupHotspotsByProximity(
  hotspots: Array<{ id: number; pitch: number; yaw: number }>
): HotspotGroup[] {
  const groups: HotspotGroup[] = [];
  const processed = new Set<number>();

  for (const hotspot of hotspots) {
    if (processed.has(hotspot.id)) continue;

    const group: HotspotGroup = {
      id: `group-${hotspot.id}`,
      centerPosition: { pitch: hotspot.pitch, yaw: hotspot.yaw },
      hotspotIds: [hotspot.id],
      objectCount: 1,
    };

    processed.add(hotspot.id);

    // Find nearby hotspots for this group
    for (const otherHotspot of hotspots) {
      if (processed.has(otherHotspot.id)) continue;

      const distance = calculateDistance(group.centerPosition, {
        pitch: otherHotspot.pitch,
        yaw: otherHotspot.yaw,
      });

      if (distance <= HOTSPOT_GROUP_THRESHOLD) {
        group.hotspotIds.push(otherHotspot.id);
        processed.add(otherHotspot.id);
        group.objectCount++;

        // Update center position to be the average
        group.centerPosition = {
          pitch:
            (group.centerPosition.pitch * (group.objectCount - 1) + otherHotspot.pitch) /
            group.objectCount,
          yaw:
            (group.centerPosition.yaw * (group.objectCount - 1) + otherHotspot.yaw) /
            group.objectCount,
        };
      }
    }

    groups.push(group);
  }

  return groups;
}

/**
 * Check if a position is near any existing hotspots
 */
export function isNearExistingHotspot(
  position: SimplePosition,
  existingHotspots: Array<{ id: number; pitch: number; yaw: number }>
): boolean {
  return findNearbyHotspots(position, existingHotspots).length > 0;
}

/**
 * Calculate the optimal position for a new hotspot near existing ones
 */
export function getOptimalGroupPosition(
  newPosition: SimplePosition,
  nearbyHotspots: Array<{ id: number; pitch: number; yaw: number }>
): SimplePosition {
  if (nearbyHotspots.length === 0) {
    return newPosition;
  }

  // Calculate center of nearby hotspots
  const totalHotspots = nearbyHotspots.length + 1; // +1 for the new position
  const avgPitch =
    (nearbyHotspots.reduce((sum, h) => sum + h.pitch, 0) + newPosition.pitch) / totalHotspots;
  const avgYaw =
    (nearbyHotspots.reduce((sum, h) => sum + h.yaw, 0) + newPosition.yaw) / totalHotspots;

  return { pitch: avgPitch, yaw: avgYaw };
}

/**
 * Convert ViewPosition to SimplePosition
 */
export function toSimplePosition(viewPosition: ViewPosition): SimplePosition {
  return { pitch: viewPosition.pitch, yaw: viewPosition.yaw };
}

/**
 * Convert SimplePosition to ViewPosition with default hfov
 */
export function toViewPosition(simplePosition: SimplePosition, hfov: number = 100): ViewPosition {
  return { pitch: simplePosition.pitch, yaw: simplePosition.yaw, hfov };
}

export const getObjectIcon = (type: HotspotObject['type']) => {
  switch (type) {
    case 'note':
      return <FileText className="h-4 w-4" />;
    case 'image':
      return <Camera className="h-4 w-4" />;
    case 'label':
      return <Tag className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

export const getObjectTypeColor = (type: HotspotObject['type']) => {
  return OBJECT_TYPE_COLORS[type]?.badge || OBJECT_TYPE_COLORS.image.badge;
};

export const getObjectTypeBackground = (type: HotspotObject['type']) => {
  return OBJECT_TYPE_COLORS[type]?.background || OBJECT_TYPE_COLORS.image.background;
};

export const getObjectTypePrimaryColor = (type: HotspotObject['type']) => {
  return OBJECT_TYPE_COLORS[type]?.primary || OBJECT_TYPE_COLORS.image.primary;
};
